import InvoiceIcon from "@assets/images/invoices.svg";
import ShippingIcon from "@assets/images/shipping-icon.svg";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from "@material-ui/core";
import DataTable from "@saleor/changeOrder/components/DataSortTable";
import MyTypography from "@saleor/components/MyTypography";
import {
  Get_Realtime_PartnerMutation,
  RealtimePartnerType
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { FormikProps } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

// import { RealtimePartnerType } from "./types";

export interface ModalAddressProps {
  onClose?: () => void;
  visible?: boolean;
  type: RealtimePartnerType;
  address: Get_Realtime_PartnerMutation["getSapRealtimePartner"]["response"];
  handleApplyAddress: (value: string, type: RealtimePartnerType) => void;
  initialValue?: string;
  formik?: FormikProps<any>;
}

export const useStyles = makeStyles(
  theme => ({
    dialogTitle: {
      textAlign: "left",
      padding: "0",
      display: "flex"
    },
    dialogTitleWrap: {
      height: "40px",
      display: " flex",
      alignItems: "center",
      lineHeight: "normal",
      marginBottom: "24px",
      marginTop: "0px"
    },
    mainTitle: {
      fontSize: "2rem",
      color: theme.palette.grey[900],
      fontWeight: 700
    },
    subTitle: {
      color: theme.palette.info.main,
      fontWeight: 400,
      fontSize: "1.6rem"
    },
    closeDialog: {
      cursor: "pointer"
    },
    root: {
      "& .MuiPaper-root": {
        maxWidth: 600,
        width: "100%",
        borderRadius: 12,
        padding: " 24px 32px"
      },
      "& .MuiDialogTitle-root": {
        padding: "0",
        display: "flex"
      },
      "& .MuiDialogActions-root": {
        borderTop: 0,
        paddingBottom: 24
      },
      "& .MuiFormControlLabel-label": {
        fontSize: "1.4rem",
        lineHeight: "22px",
        color: theme.palette.grey[900]
      },
      "& .MuiRadio-root svg": {
        width: 18,
        height: 18
      }
    },
    detailTitle: {
      fontWeight: "bold",
      marginBottom: 10,
      "& .normalText": {
        fontWeight: "normal"
      }
    },
    dialogContent: {
      maxHeight: "calc(100% - 64px)",
      overflow: "auto",
      padding: "0 !important",
      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      }
    },
    labelRadio: {
      marginBottom: "2rem"
    },
    rowlength: {
      color: "#009FB8",
      fontFamily: "SCG",
      fontSize: "20px",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "26px"
    },
    formControlLabel: {
      marginBottom: "2.5rem"
    },
    buttonAction: {
      width: "50%",
      height: 48
    },
    nameHead: {
      marginTop: 0,
      marginLeft: "12px",
      display: "flex !important",
      h2: {
        display: "flex !important"
      }
    },
    buttonWrap: {
      padding: "0px",
      marginTop: "24px"
    }
  }),
  { name: "ModalAddress" }
);

const ModalAddress: React.FC<ModalAddressProps> = props => {
  const {
    visible,
    onClose,
    type,
    address,
    handleApplyAddress,
    formik,
    initialValue
  } = props;
  const [shipping, setShipping] = useState<string>(initialValue);
  const [listAddress, setListAddress] = useState<
    Array<{
      address: string;
      partnerNo: string;
      partnerName: string;
    }>
  >([]);
  const classes = useStyles();
  const intl = useIntl();
  let fieldName;
  if (type === RealtimePartnerType.SHIP_TO) {
    fieldName = "shipTo";
  }
  if (type === RealtimePartnerType.BILL_TO) {
    fieldName = "billTo";
  }
  const [selectedRow, setSelectedRow] = useState(
    formik?.values?.[fieldName]?.split("-")[0]?.trim()
  );

  const getFormattedAddress = address => {
    const parts = address?.split("\n");
    return parts.length > 1 ? parts[1].trim() : address;
  };

  const rows = useMemo(() => {
    switch (type) {
      case RealtimePartnerType.BILL_TO:
        return listAddress.map(shippingAddress => ({
          BilltoCode: shippingAddress.partnerNo,
          billingAddress: (
            <>
              <strong>{shippingAddress.partnerName}</strong>
              <br />
              {getFormattedAddress(shippingAddress.address)}
            </>
          )
        }));
      case RealtimePartnerType.SHIP_TO:
      default:
        return listAddress.map(shippingAddress => ({
          ShiptoCode: shippingAddress.partnerNo,
          shippingAddress: (
            <>
              <strong>{shippingAddress.partnerName}</strong>
              <br />
              {getFormattedAddress(shippingAddress.address)}
            </>
          )
        }));
    }
  }, [type, listAddress]);

  const columns = useMemo(() => {
    switch (type) {
      case RealtimePartnerType.BILL_TO:
        return [
          { field: "BilltoCode", headerName: "Bill-to Code", width: 90 },
          { field: "billingAddress", headerName: "Name", width: 200 }
        ];
      case RealtimePartnerType.SHIP_TO:
      default:
        return [
          { field: "ShiptoCode", headerName: "Ship-to Code", width: 90 },
          { field: "shippingAddress", headerName: "Name", width: 200 }
        ];
    }
  }, [type]);

  const getAddressValue = e => {
    if (!e?.addressText && e?.displayText) {
      return {
        address: `${e?.displayText}`,
        partnerNo: e?.partnerNo,
        partnerName: e?.partnerName
      };
    }
    if (!e?.addressText && !e?.displayText) {
      return e;
    }

    return {
      address: `${e?.addressText}`,
      partnerNo: e?.partnerNo,
      partnerName: e?.partnerName
    };
  };

  useEffect(() => {
    if (visible) {
      const listAddressFormat = (address.length ? address : []).map(item =>
        getAddressValue(item)
      );
      let valueOption = "";
      if (type === RealtimePartnerType.SHIP_TO) {
        valueOption = formik.values?.shipTo;
      }
      if (type === RealtimePartnerType.BILL_TO) {
        valueOption = formik.values?.billTo;
      }

      const selectedAddress = listAddressFormat.find(
        item => item.address === valueOption
      );

      setShipping(selectedAddress?.address || "");
      setListAddress(listAddressFormat);
    }
  }, [visible, address]);

  const clearSelectVariant = () => {
    onClose();
    setShipping(null);
  };

  const dataRenderModal = useMemo(() => {
    switch (type) {
      case RealtimePartnerType.BILL_TO:
        return {
          iconHead: InvoiceIcon,
          nameHead: intl.formatMessage({
            defaultMessage: "Bill-to Party"
          }),
          titleHead: intl.formatMessage({
            defaultMessage: "เลือกที่อยู่สำหรับออกใบกำกับภาษี",
            description: "label"
          }),
          shippingAddres: address,
          label: intl.formatMessage({
            defaultMessage: "ข้อมูลที่อยู่สำหรับออกใบกำกับภาษีี",
            description: "label"
          })
        };
      case RealtimePartnerType.SHIP_TO:
        return {
          iconHead: ShippingIcon,
          nameHead: intl.formatMessage({
            defaultMessage: "Ship-to Party"
          }),
          titleHead: intl.formatMessage({
            defaultMessage: "เลือกสถานที่จัดส่งสินค้า",
            description: "label"
          }),
          label: intl.formatMessage({
            defaultMessage: "ข้อมูลสถานที่จัดส่ง",
            description: "label"
          })
        };

      default:
        return {
          iconHead: ShippingIcon,
          nameHead: intl.formatMessage({
            defaultMessage: "Ship-to Party"
          }),
          titleHead: intl.formatMessage({
            defaultMessage: "เลือกสถานที่จัดส่งสินค้า",
            description: "label"
          }),
          label: intl.formatMessage({
            defaultMessage: "ข้อมูลสถานที่จัดส่ง",
            description: "label"
          })
        };
    }
  }, [type, address]);

  const handleSubmit = () => {
    handleApplyAddress(shipping, type);
    clearSelectVariant();
  };
  const handleRowSelection = (selectedRowIndex: string) => {
    const selectedItem = listAddress.find(
      list => list.partnerNo === selectedRowIndex
    );
    if (selectedItem) {
      setShipping(selectedItem.address);
    }
  };
  return (
    <>
      <Dialog open={visible} onClose={onClose} className={classes.root}>
        <DialogTitle className={classes.dialogTitle}>
          <h2 className={classes.dialogTitleWrap}>
            <img src={dataRenderModal?.iconHead} alt={type.toString()} />
            <MyTypography variant={"h6"} className={classes.nameHead}>
              {dataRenderModal?.nameHead} &nbsp;
              <span className={classes.rowlength}>({rows.length})</span>
            </MyTypography>
          </h2>
          {/* <MyTypography variant="h6">{dataRenderModal?.titleHead}</MyTypography> */}
        </DialogTitle>
        <DialogContent dividers={false} className={classes.dialogContent}>
          <Grid container>
            <Grid item xs={12} md={12}>
              <DataTable
                selectedRow={selectedRow}
                setSelectedRow={setSelectedRow}
                headCells={columns}
                rows={rows}
                onRowSelect={handleRowSelection}
                sortField={
                  type === RealtimePartnerType.BILL_TO
                    ? "BilltoCode"
                    : "ShiptoCode"
                }
                boldFields={["ShiptoCode", "partnerName", "BilltoCode"]}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className={classes.buttonWrap}>
          <Button
            variant={"outlined"}
            color={"secondary"}
            className={classes.buttonAction}
            onClick={clearSelectVariant}
          >
            <FormattedMessage defaultMessage={"ยกเลิก"} />
          </Button>
          <Button
            variant={"contained"}
            color={"secondary"}
            className={classes.buttonAction}
            disabled={!Boolean(shipping)}
            onClick={handleSubmit}
          >
            <FormattedMessage defaultMessage={"ตกลง"} />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ModalAddress.displayName = "ModalAddress";
export default ModalAddress;
